import { useId, useState } from "react";
import { stateName } from "../stateNames";
import {
  EMPTY_FORM,
  MAX_NOTE,
  electionErrorMessage,
  percent,
  toSubmission,
  todayIso,
  type ElectionForm,
} from "../stateElections";
import type {
  StateCertificateEntry,
  SubmitStateWithholdingElectionInput,
} from "../types";

/**
 * EPN-52 state withholding elections: the ONE catalog-driven election form, shared by the administrator screen (PR 4) and the employee's own step (PR 5),
 * so both offer exactly the fields the state's catalog entry takes and apply the same client checks. The server checks everything again.
 * `audience` only changes the wording and whether a date before today may be chosen (an employee picks today or later, SME-36 default).
 */
export function StateElectionForm({
  stateCode,
  entry,
  audience,
  send,
  onSubmitted,
  onCancel,
}: {
  stateCode: string;
  entry: StateCertificateEntry;
  audience: "admin" | "employee";
  /** Sends the checked request; rejects with the API error when the server refuses. */
  send: (input: SubmitStateWithholdingElectionInput) => Promise<unknown>;
  onSubmitted: (message: string) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<ElectionForm>(EMPTY_FORM);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const id = useId();
  const set = <K extends keyof ElectionForm>(key: K, value: ElectionForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const admin = audience === "admin";
  const today = todayIso();
  const backdated = form.effectiveFrom !== "" && form.effectiveFrom < today;
  const exempt = entry.allowsExempt && form.isExempt;
  const name = stateName(stateCode);
  const subject = admin ? "The employee" : "I";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const { input, problem: p } = toSubmission(
      stateCode,
      entry,
      form,
      today,
      admin,
    );
    if (!input) {
      setProblem(p ?? "Check the form and try again.");
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await send(input);
      onSubmitted(
        admin
          ? `${name} election submitted. It is not used until another reviewer approves it.`
          : `Your ${name} election was submitted. Your payroll team reviews it before it is used.`,
      );
    } catch (err) {
      setProblem(
        electionErrorMessage(
          err,
          "The election could not be submitted. Please try again.",
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="recip-review__form"
      onSubmit={(e) => void submit(e)}
      aria-label={
        admin
          ? `Enter ${name} withholding election`
          : `Your ${name} withholding election`
      }
      aria-describedby={problem ? `${id}-problem` : undefined}
      noValidate
    >
      <label className="recip-review__field">
        Takes effect on
        <input
          type="date"
          value={form.effectiveFrom}
          min={admin ? undefined : today}
          onChange={(e) => set("effectiveFrom", e.target.value)}
          required
          aria-required="true"
          data-autofocus
        />
      </label>
      {admin && backdated && (
        <label className="recip-review__field">
          Why does it take effect before today? (required)
          <textarea
            rows={2}
            maxLength={MAX_NOTE}
            value={form.backdateReason}
            onChange={(e) => set("backdateReason", e.target.value)}
          />
        </label>
      )}
      {entry.allowsExempt && (
        <label className="recip-review__decision">
          <input
            type="checkbox"
            checked={form.isExempt}
            onChange={(e) => set("isExempt", e.target.checked)}
          />
          {subject} {admin ? "claims" : "claim"} exemption from {name}{" "}
          withholding
        </label>
      )}
      {!exempt && entry.filingStatuses.length > 0 && (
        <label className="recip-review__field">
          Filing status
          {entry.status === "CertificateRequired" ? " (required)" : ""}
          <select
            value={form.filingStatusKey}
            onChange={(e) => set("filingStatusKey", e.target.value)}
          >
            <option value="">Choose…</option>
            {entry.filingStatuses.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {!exempt && entry.allowsExemptions && (
        <label className="recip-review__field">
          Exemptions
          <input
            inputMode="decimal"
            value={form.exemptions}
            onChange={(e) => set("exemptions", e.target.value)}
          />
        </label>
      )}
      {!exempt && entry.allowsExemptionAmount && (
        <label className="recip-review__field">
          Exemption amount (dollars)
          <input
            inputMode="decimal"
            value={form.exemptionAmount}
            onChange={(e) => set("exemptionAmount", e.target.value)}
          />
        </label>
      )}
      {!exempt && entry.allowsDependents && (
        <label className="recip-review__field">
          Dependents
          <input
            inputMode="numeric"
            value={form.dependents}
            onChange={(e) => set("dependents", e.target.value)}
          />
        </label>
      )}
      {!exempt && entry.allowsAdditionalWithholding && (
        <label className="recip-review__field">
          Extra withholding each pay period (dollars)
          <input
            inputMode="decimal"
            value={form.additionalWithholding}
            onChange={(e) => set("additionalWithholding", e.target.value)}
          />
        </label>
      )}
      {!exempt && entry.allowsElectedRate && (
        <label className="recip-review__field">
          {admin
            ? "Withholding rate the employee chose"
            : "Withholding rate you choose"}
          <select
            value={form.electedRate}
            onChange={(e) => set("electedRate", e.target.value)}
          >
            <option value="">
              {entry.defaultElectedRate !== null
                ? `None chosen (the state's default, ${percent(entry.defaultElectedRate)}, applies)`
                : "None chosen"}
            </option>
            {entry.electedRates.map((r) => (
              <option key={r} value={String(r)}>
                {percent(r)}
              </option>
            ))}
          </select>
        </label>
      )}
      {!exempt && entry.allowsLowerRateElection && (
        <label className="recip-review__decision">
          <input
            type="checkbox"
            checked={form.lowerRateElection}
            onChange={(e) => set("lowerRateElection", e.target.checked)}
          />
          {admin
            ? "The employee chose the lower rate"
            : "I choose the lower rate"}
        </label>
      )}
      {exempt && (
        <p className="muted">
          {admin ? "An exempt employee has" : "If you are exempt, you have"} no
          other withholding answers for this state.
        </p>
      )}
      {problem && (
        <p className="error" role="alert" id={`${id}-problem`}>
          {problem}
        </p>
      )}
      <p className="muted">
        {admin
          ? "Submitting does not change payroll. Another reviewer must approve it first, and approved elections are used only by the staging tax preview."
          : "Submitting does not change your pay right away. Your payroll team reviews it first."}
      </p>
      <div className="recip-review__dialog-actions">
        <button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" disabled={saving}>
          {saving ? "Submitting…" : "Submit for review"}
        </button>
      </div>
    </form>
  );
}
